import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { appConstants } from '../constants/app-constants';
import { IApiResponse } from '../models/api-response';
import { ITokenDetails } from '../models/token-details';
import { IUser, User } from '../models/user';
import { UserUtility } from '../utilities/user-utility';
import { SamlService } from './saml.service';
import { OKTA_ORIGINS, OKTA_PATH, SALESFORCE_ALLOWLIST, ALLOWED_PARAMS } from '../constants/auth-constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  _authApiBaseUrl = environment.servicesUrl.authentication.endsWith('/')
    ? `${environment.servicesUrl.authentication}auth`
    : `${environment.servicesUrl.authentication}/auth`;

  private _currentUser: IUser | null = null;
  private _isOktaUser: boolean = false;
  currentUserSubject: BehaviorSubject<IUser | null> =
    new BehaviorSubject<IUser | null>(null);

  private isLoggingOut = false;

  constructor(
    private http: HttpClient,
    private _samlService: SamlService,
    private _location: Location
  ) {}

  get authToken(): string | null {
    return localStorage.getItem(appConstants.localStorageKey.token);
  }

  get isLoggedIn(): Observable<boolean> {
    return this.getUserInfo().pipe(
      map((_) => (this._currentUser ? true : false))
    );
  }

  get isInternalUser(): boolean {
    return UserUtility.isInternalUser(this._currentUser);
  }

  get user$(): Observable<IUser | null> {
    return this.currentUserSubject.asObservable();
  }

  hasRoles(...roles: string[]): Observable<boolean> {
    if (!this._currentUser) {
      return this.getUserInfo().pipe(
        map((user) => UserUtility.hasRoles(user, ...roles))
      );
    }

    return new Observable((observer) => {
      observer.next(UserUtility.hasRoles(this._currentUser, ...roles));
      observer.complete();
    });
  }

  get isOktaUser(): boolean {
    const tokenDetails = this.extractAuthTokenDetails();
    if (tokenDetails) {
      this._isOktaUser = tokenDetails.iss === environment.oktaIssuer;
    }
    return this._isOktaUser;
  }

  private setUser(tokenDetails: any) {
    if (!tokenDetails) return;

    this._isOktaUser = tokenDetails.iss === environment.oktaIssuer;

    let user = new User();
    user.userId = tokenDetails.sub;
    user.userName = tokenDetails.UserName;
    user.title = tokenDetails.Title;
    user.firstName = tokenDetails.FirstName;
    user.lastName = tokenDetails.LastName;
    user.email = tokenDetails.Mail;
    user.role = tokenDetails.Role;
    user.committees = tokenDetails.Committees;
    user.objectId = tokenDetails.ObjectId;
    user.sessionId = tokenDetails.SessionId;
    user.sessionExpiry = tokenDetails.expires;
    user.userType = tokenDetails.UserType;
    user.userIdCustom = tokenDetails.UserIdCustom;
    user.partnerServerURL = tokenDetails.PartnerServerURL;
    user.features = tokenDetails.Features;

    this._currentUser = user;
    this.currentUserSubject.next(user);
  }

  private extractAuthTokenDetails(): any {
    if (this.authToken) {
      try {
        return jwtDecode(this.authToken);
      } catch {
        console.error('Invalid token');
      }
    }

    return undefined;
  }

  private handleTokenDetailsResponse(response: IApiResponse<ITokenDetails>) {
    if (response.success) {
      localStorage.setItem(
        appConstants.localStorageKey.token,
        response.content.token
      );

      const tokenDetails = this.extractAuthTokenDetails();
      if (tokenDetails) {
        this.setUser(tokenDetails);
      } else {
        this.logout();
      }
    } else {
      this.logout();
    }
  }

  getUserInfo(): Observable<IUser | null> {
    const tokenDetails = this.extractAuthTokenDetails();
    if (!tokenDetails || this.isTokenExpired()) {
      this.redirectExpiredSessionToLogin();
      return of(null);
    }

    return new Observable((observer) => {
      this.setUser(tokenDetails);
      observer.next(this._currentUser);
      observer.complete();
    });

    // TODO: for new auth in the future - jwt details will be limited so there should be an endpoint to get current user info/details
  }

  isTokenExpired(): boolean {
    const tokenDetails = this.extractAuthTokenDetails();
    if (!tokenDetails) return true;

    const currentTime = Date.now() / 1000;
    return tokenDetails.exp < currentTime;
  }

  getTokens() {
    return this.http
      .get<IApiResponse<ITokenDetails>>(this._authApiBaseUrl, {
        withCredentials: true,
      })
      .pipe(
        map((response) => {
          this.handleTokenDetailsResponse(response);
          return response;
        })
      );
  }

  refreshAccessToken(): Observable<IApiResponse<ITokenDetails>> {
    return this.http
      .post<IApiResponse<ITokenDetails>>(
        `${this._authApiBaseUrl}/refresh`,
        null,
        { withCredentials: true }
      )
      .pipe(
        map((response) => {
          this.handleTokenDetailsResponse(response);
          return response;
        })
      );
  }

  redirectExpiredSessionToLogin(returnUrl: string | null = null) {
    localStorage.removeItem(appConstants.localStorageKey.token);
    this._currentUser = null;
    this.currentUserSubject.next(null);
    this.redirectToLoginPage(returnUrl);
  }

  redirectToLoginPage(returnUrl: string | null = null) {
    window.location.href = this.buildLoginUrl(returnUrl);
  }

  private buildLoginUrl(returnUrl: string | null): string {
    const currentRoute = window.location.hash.replace(/^#\/?/, '');
    const landingPage = (returnUrl || currentRoute || 'dashboard').replace(/^\/+/, '');
    const loginUrl = new URL('SAML/SSO', environment.servicesUrl.authentication);
    loginUrl.searchParams.set('landingPage', landingPage);

    return loginUrl.toString();
  }

  logoutWithReturnUrl() {
    const locationPath = this._location.path();
    const landingPage =
      locationPath && locationPath.length > 0 ? locationPath : null;
    this.logout(landingPage);
  }

  logout(returnUrl: string | null = null) {
    if (this.isLoggingOut) return;

    this.isLoggingOut = true;
    const accessToken = this.authToken;
    const tokenHasExpired = this.isTokenExpired();

    this.http
      .post<IApiResponse<string>>(`${this._authApiBaseUrl}/logout`, null, {
        withCredentials: true,
      })
      .subscribe((response) => {
        localStorage.clear();
        this._currentUser = null;

        if (!accessToken || tokenHasExpired) {
          this.redirectToLoginPage(returnUrl);
          return;
        }

        if (!response.content) {
          this.redirectToLoginPage(returnUrl);
          return;
        }

        let url: URL;
        try {
          url = new URL(response.content);
        } catch {
          console.error('Invalid logout redirect URL');
          return;
        }

        const matchedOktaOrigin = OKTA_ORIGINS.find(origin => origin === url.origin);
        if (matchedOktaOrigin && url.pathname.startsWith(OKTA_PATH)) {
          const oktaLogoutUrl = `${matchedOktaOrigin}${url.pathname}${url.search}`;
          window.location.assign(oktaLogoutUrl);
          return;
        }

        const matchedSalesforceOrigin = SALESFORCE_ALLOWLIST.find(origin => origin === url.origin);

        if (!matchedSalesforceOrigin) {
          console.error(
            'Blocked logout redirect to untrusted origin:',
            url.origin
          );
          return;
        }

        const searchParams = new URLSearchParams();

        url.searchParams.forEach((value, key) => {
          if (ALLOWED_PARAMS.includes(key)) {
            searchParams.append(key, value);
          }
        });

        window.location.assign(
          `${matchedSalesforceOrigin}${url.pathname}?${searchParams.toString()}`
        );
      });
  }

  isUserPmOrAboveOrHoSD(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const currentUserSubscription = this.currentUserSubject.subscribe({
        next: (user) => {
          if (user) {
            const isUserPmOrAbove = UserUtility.userIsPmOrAbove(user);

            if (!isUserPmOrAbove) {
              this._samlService.getLoggedInUserHoSDStatus().subscribe({
                next: (response) => {
                  const isHoSD = response.content;
                  observer.next(isUserPmOrAbove || isHoSD);
                  observer.complete();
                },
                error: (err) => {
                  console.error('Error fetching HoSD status:', err);
                  observer.next(false);
                  observer.complete();
                },
              });
            } else {
              observer.next(true);
              observer.complete();
            }
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: (err) => {
          console.error('Error with currentUserSubject:', err);
          observer.next(false);
          observer.complete();
        },
      });

      return () => {
        currentUserSubscription.unsubscribe();
      };
    });
  }
}
